import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserService } from '../core/services/user.service';
import { OrganizationSummaryDto } from '../core/api-client/models';
import { MaterialModule } from '../shared/material.module';
import { CommonModule } from '@angular/common';
import { DateAdapter } from '@angular/material/core';
import { ModalService } from '../core/services/modal.service';
import { RealtimeService } from '../core/services/realtime.service';
import { SessionService } from '../core/services/session.service';
import { EMPTY, filter, finalize, from, Subscription, switchMap } from 'rxjs';

type LangCode = 'en' | 'ro';

@Component({
  standalone: true,
  selector: 'app-shell-layout',
  imports: [
    RouterOutlet, RouterLink, RouterLinkActive,
    CommonModule,
    MaterialModule,

    TranslateModule
  ],
  templateUrl: './shell-layout.component.html',
  styleUrl: './shell-layout.component.scss',
})
export class ShellLayoutComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  readonly user = inject(UserService);
  private readonly dateAdapter = inject(DateAdapter);
  private readonly modalService = inject(ModalService);
  private readonly sessionService = inject(SessionService);
  private readonly realtimeService = inject(RealtimeService);

  private sub = new Subscription();
  // TODO: Replace with API data after login
  organizations: OrganizationSummaryDto[] = [];

  currentLang: LangCode = this.getSavedLangOrDefault();
  loading: boolean = false;

  constructor() {
    this.translate.addLangs(['en', 'ro']);
    this.translate.setDefaultLang('en');
    this.translate.use(this.currentLang);
  }
  ngOnInit(): void {
    this.organizations = this.user.getOrganizations();
    const locale = this.currentLang === 'ro' ? 'ro-RO' : 'en-US';
    this.dateAdapter.setLocale(locale);

    this.sub.add(
          this.realtimeService.orgEvents$.subscribe(e => {
            switch (e.eventName) {
              case 'datasource.created': {
                break;
              }
              case 'datasource.updated': {
                break;
              }
              case 'datasource.deleted': {
                break;
              }
            }
          })
        );
  }

  async switchOrganization(org: OrganizationSummaryDto): Promise<void> {
    if (this.user.getSelectedOrganization()?.id === org.id) 
      return;

    from(this.modalService.switchOrganizationFromShellModal(org.name)).pipe(
      switchMap(confirmed => {
        if (!confirmed) 
          return EMPTY;                // user cancelled

        return this.sessionService.switchOrganization(org.id!);
      }),
      finalize(() => { 
        this.loading = false; 
      })
    ).subscribe({
      next: () => window.location.reload(),
      error: err => console.error('Failed to switch organization', err)
    });
  }

  setLang(lang: LangCode): void {
    if (lang === this.currentLang)
      return;

    this.currentLang = lang;
    localStorage.setItem('lang', lang);
    this.translate.use(lang);

    const locale = lang === 'ro' ? 'ro-RO' : 'en-US';
    this.dateAdapter.setLocale(locale);
  }

  logout(): void {
    this.sessionService.logout().subscribe({
      error: (err) => {
        console.error('Logout failed', err);
      }
    });
  }

  private getSavedLangOrDefault(): LangCode {
    const v = localStorage.getItem('lang');
    return v === 'ro' || v === 'en' ? v : 'en';
  }
}
